import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Search,
  Plus,
  Eye,
  EyeOff,
  Check,
  X,
  User as UserIcon,
  Mail,
  Phone,
  Shield,
  Users,
  KeyRound,
  Loader2,
  Calendar,
} from "lucide-react";
import { CommonPagination } from "../../components/CommonPagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { superAdminUsersAPI, type UserListItem } from "../../services/api";
import { validatePassword } from "@/lib/passwordValidation";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../lib/utils";

export interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  joinedOn: string;
  phone_number?: string;
  // New fields based on the provided JSX and API response structure
  full_name?: string;
  jbf_role?: string;
}

function getUserDisplayName(item: UserListItem): string {
  return (
    item.full_name?.trim() ??
    [item.first_name, item.last_name].filter(Boolean).join(" ").trim() ??
    item.email ??
    "—"
  );
}

function toUserRow(item: UserListItem): UserRow {
  const name =
    item.full_name?.trim() ??
    [item.first_name, item.last_name].filter(Boolean).join(" ").trim() ??
    "-";
  return {
    id: item.id,
    name,
    email: item.email,
    role: item.jbf_role ?? "—",
    joinedOn: formatDate(item.created_on),
    phone_number: item.phone_number,
    full_name: item.full_name, // Add full_name
    jbf_role: item.jbf_role, // Add jbf_role
  };
}

const ADD_USER_ROLES = [
  { value: "student", label: "Student" },
  { value: "institutions_admin", label: "Institution Admin" },
  { value: "management", label: "Management" },
  { value: "trustee", label: "Trustee" },
  { value: "donor", label: "Donor" },
  { value: "hostel_admin", label: "Hostel Admin" },
] as const;

export default function UserManagement() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone_number: "",
    jbf_role: "",
    designation: "",
  });
  const [viewUserId, setViewUserId] = useState<string | null>(null);
  const [viewUserDetail, setViewUserDetail] = useState<UserListItem | null>(
    null
  );
  const [viewLoading, setViewLoading] = useState(false);
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { can } = useAuth();
  const canWrite = can("users", "write");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        jbf_role: roleFilter !== "all" ? roleFilter : undefined,
      };
      const res = await superAdminUsersAPI.list(params);
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setUsers(Array.isArray(list) ? list.map(toUserRow) : []); // Keep toUserRow mapping
        const count =
          res.count ??
          (res as any).total_count ??
          (Array.isArray(list) ? list.length : 0);
        setTotalItems(count);
        setTotalPages(Math.ceil(count / pageSize));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, roleFilter]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm, roleFilter, currentPage, pageSize, fetchUsers]); // Added fetchUsers to dependencies

  const resetAddUserForm = () => {
    setAddUserForm({
      full_name: "",
      email: "",
      password: "",
      phone_number: "",
      jbf_role: "",
      designation: "",
    });
  };

  const handleAddUser = async () => {
    const { full_name, email, password, phone_number, jbf_role, designation } =
      addUserForm;
    if (!full_name.trim()) {
      toast.error("Please enter full name");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter email");
      return;
    }
    if (!password) {
      toast.error("Please enter password");
      return;
    }
    const passwordValidation = validatePassword(password.trim(), {
      email: email.trim(),
      fullName: full_name.trim(),
    });
    if (!passwordValidation.valid) {
      toast.error(passwordValidation.errors[0] ?? "Invalid password");
      return;
    }
    if (!jbf_role) {
      toast.error("Please select a role");
      return;
    }
    setAddUserLoading(true);
    try {
      await superAdminUsersAPI.create({
        email: email.trim(),
        password: password.trim(),
        full_name: full_name.trim(),
        phone_number: phone_number.trim() || undefined,
        jbf_role,
        ...(designation.trim() ? { designation: designation.trim() } : {}),
      });
      toast.success("User added successfully!");
      setIsDialogOpen(false);
      resetAddUserForm();
      await fetchUsers(); // Re-fetch users to update the list and pagination
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Failed to add user.";
      toast.error(msg);
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleViewUser = useCallback(async (user: UserRow) => {
    setViewUserId(user.id);
    setViewUserDetail(null);
    setViewLoading(true);
    try {
      const res = await superAdminUsersAPI.get(user.id);
      const detail =
        res && typeof res === "object" && "data" in res
          ? (res as { data: UserListItem }).data
          : res;
      if (detail?.id) {
        setViewUserDetail(detail);
      } else {
        toast.error("Failed to load user details");
        setViewUserId(null);
      }
    } catch {
      toast.error("Failed to load user details");
      setViewUserId(null);
    } finally {
      setViewLoading(false);
    }
  }, []);

  const handleCloseViewDialog = () => {
    setViewUserId(null);
    setViewUserDetail(null);
  };

  const userTableColumns = useMemo<TableColumnDef<UserRow>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        lockVisibility: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "user",
        label: "User Name",
        width: 200,
        renderCell: user => (
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <UserIcon className="h-4 w-4 text-primary" />
            </div>
            <span className="font-medium">{user.full_name ?? user.email}</span>
          </div>
        ),
      },
      {
        id: "email",
        label: "Email",
        width: 200,
        renderCell: user => user.email,
      },
      {
        id: "mobile",
        label: "Mobile Number",
        width: 140,
        renderCell: user => user.phone_number ?? "—",
      },
      {
        id: "role",
        label: "Profile Status",
        width: 140,
        renderCell: user => (
          <Badge variant="outline" className="capitalize">
            {user.jbf_role?.replace("_", " ") ?? "—"}
          </Badge>
        ),
      },
      {
        id: "joined",
        label: "Created Date",
        width: 130,
        renderCell: user => user.joinedOn,
      },
      {
        id: "actions",
        label: "Actions",
        width: 120,
        headerClassName: "text-right",
        cellClassName: "text-right",
        renderCell: user => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewUser(user)}
            title="View Details"
          >
            <Eye className="mr-1 h-4 w-4" />
            View
          </Button>
        ),
      },
    ],
    [currentPage, pageSize, handleViewUser]
  );

  const handleDeleteUser = async () => {
    if (!deleteUser) {
      return;
    }
    setDeleteLoading(true);
    try {
      await superAdminUsersAPI.delete(deleteUser.id);
      toast.success("User deleted successfully!");
      setDeleteUser(null);
      await fetchUsers(); // Re-fetch users to update the list and pagination
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error?.message ??
        error?.message ??
        "Failed to delete user.";
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            User Management
          </h1>
          <p className="text-muted-foreground">
            Manage all system users and their roles
          </p>
        </div>
        {canWrite && (
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetAddUserForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="userName">Full Name</Label>
                  <Input
                    id="userName"
                    placeholder="Enter full name"
                    value={addUserForm.full_name}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        full_name: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="userEmail">Email</Label>
                  <Input
                    id="userEmail"
                    type="email"
                    placeholder="user@example.com"
                    value={addUserForm.email}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="userPhone">Phone Number</Label>
                  <Input
                    id="userPhone"
                    placeholder="Enter 10-digit phone"
                    value={addUserForm.phone_number}
                    maxLength={10}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        phone_number: e.target.value.replace(/\D/g, ""),
                      }))
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="userPassword">Password</Label>
                  <div className="relative">
                    <Input
                      id="userPassword"
                      type={showAddUserPassword ? "text" : "password"}
                      placeholder="Enter password (8-10 characters)"
                      value={addUserForm.password}
                      onChange={e =>
                        setAddUserForm(prev => ({
                          ...prev,
                          password: e.target.value.slice(0, 10),
                        }))
                      }
                      maxLength={10}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowAddUserPassword(prev => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      aria-label={
                        showAddUserPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showAddUserPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {addUserForm.password && (
                    <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 text-xs">
                      <p className="mb-2 font-medium text-muted-foreground">
                        Password requirements
                      </p>
                      <ul className="space-y-1">
                        {validatePassword(addUserForm.password, {
                          email: addUserForm.email,
                          fullName: addUserForm.full_name,
                        }).hints.map(h => (
                          <li
                            key={h.rule}
                            className={`flex items-center gap-2 ${h.met ? "text-green-600" : "text-muted-foreground"}`}
                          >
                            {h.met ? (
                              <Check className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <X className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <span>{h.rule}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="userRole">Role</Label>
                  <Select
                    value={addUserForm.jbf_role}
                    onValueChange={value =>
                      setAddUserForm(prev => ({ ...prev, jbf_role: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ADD_USER_ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="userDesignation">Designation</Label>
                  <Input
                    id="userDesignation"
                    placeholder="Enter designation (optional)"
                    value={addUserForm.designation}
                    onChange={e =>
                      setAddUserForm(prev => ({
                        ...prev,
                        designation: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={addUserLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddUser} disabled={addUserLoading}>
                    {addUserLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Adding...
                      </span>
                    ) : (
                      "Add User"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="institutions_admin">
                  Institution Admin
                </SelectItem>
                <SelectItem value="management">Management</SelectItem>
                <SelectItem value="trustee">Trustee</SelectItem>
                <SelectItem value="donor">Donor</SelectItem>
                <SelectItem value="hostel_admin">Hostel Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-3 pb-4">
          <DataTable
            tableId="super-admin-user-management"
            title={`All Users (${totalItems})`}
            columns={userTableColumns}
            data={users}
            getRowKey={user => user.id}
            loading={loading}
            loadingMessage="Loading users..."
            emptyMessage="No users found."
            headerRowClassName="bg-muted/30"
          />

          {totalItems > 0 && (
            <div className="border-t p-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* View User Dialog */}
      <Dialog
        open={viewUserId !== null}
        onOpenChange={open => !open && handleCloseViewDialog()}
      >
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
          <DialogHeader className="border-b border-zinc-200 bg-white px-6 pb-4 pr-14 pt-5 text-left">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <DialogTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight text-zinc-900">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]"
                  aria-hidden
                >
                  <UserIcon
                    className="h-5 w-5 text-[#8B3232]"
                    strokeWidth={2.25}
                  />
                </span>
                User Details
              </DialogTitle>
              {viewUserDetail && !viewLoading && viewUserDetail.jbf_role && (
                <span className="inline-flex items-center gap-2 rounded-full border border-[#f0dede] bg-[#FDF7F7] px-3.5 py-1 text-sm font-semibold capitalize text-[#8B3232]">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-[#c53030]"
                    aria-hidden
                  />
                  {viewUserDetail.jbf_role.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </DialogHeader>

          {viewLoading ? (
            <div className="flex h-[400px] flex-col items-center justify-center gap-4 bg-white">
              <Loader2 className="h-12 w-12 animate-spin text-[#8B3232]" />
              <p className="animate-pulse font-medium text-muted-foreground">
                Fetching user details...
              </p>
            </div>
          ) : (
            viewUserDetail && (
              <>
                <div className="max-h-[min(80vh,730px)] overflow-y-auto bg-white">
                  <div className="border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5">
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                      <div className="min-w-0 space-y-1 sm:flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                          Full name
                        </p>
                        <p className="text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl">
                          {getUserDisplayName(viewUserDetail)}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12 md:gap-x-16">
                        {/* <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Email
                          </p>
                          <p className="text-sm font-semibold text-zinc-900">
                            {viewUserDetail.email}
                          </p>
                        </div> */}
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Joined on
                          </p>
                          <p className="text-sm font-semibold text-zinc-900">
                            {formatDate(viewUserDetail.created_on)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                    <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <UserIcon className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Account information
                      </h3>
                      <hr className="mb-4 border-zinc-200" />
                      <div className="grid gap-5">
                        <DetailItem
                          icon={<UserIcon className="h-3.5 w-3.5" />}
                          label="Full name"
                          value={getUserDisplayName(viewUserDetail)}
                        />
                        <DetailItem
                          icon={<Mail className="h-3.5 w-3.5" />}
                          label="Email address"
                          value={viewUserDetail.email}
                        />
                        <DetailItem
                          icon={<Phone className="h-3.5 w-3.5" />}
                          label="Phone number"
                          value={viewUserDetail.phone_number ?? "—"}
                        />
                        <DetailItem
                          icon={<Calendar className="h-3.5 w-3.5" />}
                          label="Joined on"
                          value={formatDate(viewUserDetail.created_on)}
                        />
                      </div>
                    </div>

                    <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <Shield className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Role & access
                      </h3>
                      <hr className="mb-4 border-zinc-200" />
                      <div className="grid gap-5">
                        <DetailItem
                          icon={<Shield className="h-3.5 w-3.5" />}
                          label="JBF role"
                          value={
                            viewUserDetail.jbf_role?.replace(/_/g, " ") ?? "—"
                          }
                        />
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-400">
                            <Users className="h-3.5 w-3.5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              Groups
                            </p>
                            {viewUserDetail.groups &&
                            viewUserDetail.groups.length > 0 ? (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {viewUserDetail.groups.map(g => (
                                  <Badge
                                    key={g.id}
                                    variant="secondary"
                                    className="font-medium"
                                  >
                                    {g.name}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm font-semibold text-zinc-900">
                                —
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 md:col-span-2">
                      <h3 className="flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <KeyRound className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Permissions
                      </h3>
                      <div className="overflow-hidden rounded-lg border border-zinc-200">
                        <div className="h-[200px] overflow-x-auto overflow-y-auto">
                          <Table>
                            <TableHeader className="sticky top-0 z-10 bg-zinc-50/95 backdrop-blur-sm">
                              <TableRow className="border-b border-zinc-200 hover:bg-zinc-50/95">
                                <TableHead className="h-10 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
                                  #
                                </TableHead>
                                <TableHead className="h-10 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
                                  Permission
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {!viewUserDetail.permissions?.length ? (
                                <TableRow>
                                  <TableCell
                                    colSpan={2}
                                    className="py-8 text-center text-muted-foreground"
                                  >
                                    No permissions assigned.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                viewUserDetail.permissions.map((p, idx) => (
                                  <TableRow key={p} className="border-zinc-100">
                                    <TableCell className="h-10 w-12 font-medium text-zinc-500">
                                      {idx + 1}
                                    </TableCell>
                                    <TableCell className="h-10 font-medium text-zinc-800">
                                      {p}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DialogFooter className="gap-3 border-t border-zinc-200 bg-white px-6 py-4 sm:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                    onClick={handleCloseViewDialog}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteUser}
        onOpenChange={open => !open && setDeleteUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          {deleteUser && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{deleteUser.name}</strong> ({deleteUser.email})? This
                action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteUser(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-400 [&_svg]:text-zinc-400">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <p className="text-sm font-semibold text-zinc-900">{value}</p>
      </div>
    </div>
  );
}
